import { jsPDF } from "jspdf";

export const generateAdminExportPdf = (
  row,
  sections,
  spiderLabels,
  spiderValues,
  responses,
) => {
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = 210;
  const H = 297;
  const M = 18;
  const CW = W - M * 2;
  // eslint-disable-next-line
  const textAreaW = CW - 36; // left text area avoids badge

  const overallScore =
    sections.length > 0
      ? (sections.reduce((a, s) => a + s.score, 0) / sections.length).toFixed(1)
      : "—";

  const dateStr = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // ── HEADER BAR ──────────────────────────────────────────────────
  pdf.setFillColor(38, 56, 66);
  pdf.rect(0, 0, W, 48, "F");

  pdf.setTextColor(0, 156, 204);
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "bold");
  pdf.text("CITY TO CITY ASIA PACIFIC", M, 11);

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(18);
  pdf.setFont("helvetica", "bold");
  pdf.text("GCCHF Church Health Report", M, 22);

  pdf.setTextColor(154, 164, 175);
  pdf.setFontSize(10);
  pdf.setFont("helvetica", "normal");
  pdf.text(row.name, M, 32);

  pdf.setFontSize(8.5);
  pdf.setTextColor(120, 135, 148);
  pdf.text(`Generated: ${dateStr}`, M, 41);

  // Overall score badge — right side, vertically centred in header
  const badgeX = W - M - 32;
  const badgeY = 8;
  const badgeW = 32;
  const badgeH = 32;
  pdf.setFillColor(255, 204, 50);
  pdf.roundedRect(badgeX, badgeY, badgeW, badgeH, 3, 3, "F");
  pdf.setTextColor(38, 56, 66);
  pdf.setFontSize(22);
  pdf.setFont("helvetica", "bold");
  pdf.text(String(overallScore), badgeX + badgeW / 2, badgeY + 16, {
    align: "center",
  });
  pdf.setFontSize(7.5);
  pdf.setFont("helvetica", "normal");
  pdf.text("out of 5.0", badgeX + badgeW / 2, badgeY + 22, { align: "center" });
  pdf.setFontSize(6.5);
  pdf.setTextColor(80, 84, 93);
  pdf.text("OVERALL SCORE", badgeX + badgeW / 2, badgeY + 28, {
    align: "center",
  });

  // ── USER INFO BLOCK ─────────────────────────────────────────────
  let y = 56;
  pdf.setFillColor(248, 250, 252);
  pdf.roundedRect(M, y, CW, 32, 2, 2, "F");
  pdf.setDrawColor(226, 232, 240);
  pdf.roundedRect(M, y, CW, 32, 2, 2, "S");

  pdf.setTextColor(30, 32, 44);
  pdf.setFontSize(12);
  pdf.setFont("helvetica", "bold");
  pdf.text(row.name, M + 4, y + 9);

  pdf.setFontSize(9.5);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(107, 114, 128);
  pdf.text(row.email || "—", M + 4, y + 17);
  pdf.text(`Church: ${row.church || "—"}`, M + 4, y + 24);
  pdf.text(`Location: ${row.city || row.location || "—"}`, M + 4, y + 30);

  y += 42;

  // ── SCORE INTERPRETATION BAND ───────────────────────────────────
  const bands = [
    { label: "Thriving", range: "4.0–5.0", r: 58, g: 92, b: 58 },
    { label: "Growing", range: "3.0–3.9", r: 107, g: 80, b: 15 },
    { label: "Developing", range: "2.0–2.9", r: 107, g: 58, b: 16 },
    { label: "Still Forming", range: "0–1.9", r: 107, g: 42, b: 42 },
  ];
  pdf.setFillColor(248, 250, 252);
  pdf.rect(M, y, CW, 11, "F");
  let bx = M + 3;
  bands.forEach((b) => {
    pdf.setFillColor(b.r, b.g, b.b);
    pdf.circle(bx + 2, y + 5.5, 1.8, "F");
    pdf.setTextColor(55, 65, 81);
    pdf.setFontSize(8.5);
    pdf.setFont("helvetica", "bold");
    pdf.text(b.label, bx + 6, y + 7);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(90, 100, 110);
    // add explicit 3mm gap between label and range
    pdf.text(b.range, bx + 6 + pdf.getTextWidth(b.label) + 3, y + 7);
    bx += CW / bands.length;
  });
  y += 19;

  // ── SECTION AVERAGES ────────────────────────────────────────────
  pdf.setTextColor(38, 56, 66);
  pdf.setFontSize(13);
  pdf.setFont("helvetica", "bold");
  pdf.text("Section Averages", M, y);
  y += 3;
  pdf.setDrawColor(38, 56, 66);
  pdf.setLineWidth(0.4);
  pdf.line(M, y, W - M, y);
  y += 9;

  sections.forEach((s) => {
    if (y > H - 55) {
      pdf.addPage();
      y = 20;
    }

    pdf.setTextColor(30, 32, 44);
    pdf.setFontSize(10.5);
    pdf.setFont("helvetica", "bold");
    pdf.text(s.name, M, y);
    pdf.text(`${s.score} / 5`, W - M, y, { align: "right" });

    const statusLabel =
      s.score >= 4 ? "Thriving" : s.score >= 3 ? "Growing" : "Needs Focus";
    const statusR = s.score >= 4 ? 58 : 107;
    const statusG = s.score >= 4 ? 92 : s.score >= 3 ? 80 : 42;
    const statusB = s.score >= 4 ? 58 : s.score >= 3 ? 15 : 42;
    pdf.setTextColor(statusR, statusG, statusB);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.text(statusLabel, W - M - pdf.getTextWidth(`${s.score} / 5`) - 16, y);

    y += 6;

    // Progress bar
    pdf.setFillColor(229, 231, 235);
    pdf.roundedRect(M, y, CW, 4, 1, 1, "F");
    pdf.setFillColor(38, 56, 66);
    pdf.roundedRect(M, y, CW * (s.score / 5), 4, 1, 1, "F");
    y += 10; // extra space below bar

    y += 10;
  });

  // ── SUBSECTION SCORES ───────────────────────────────────────────
  const answeredSubsections = spiderLabels
    .map((label, i) => ({ label, value: spiderValues[i] }))
    .filter((s) => s.value > 0);

  if (answeredSubsections.length > 0) {
    if (y > H - 60) {
      pdf.addPage();
      y = 20;
    }
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.3);
    pdf.line(M, y, W - M, y);
    y += 10;

    pdf.setTextColor(38, 56, 66);
    pdf.setFontSize(13);
    pdf.setFont("helvetica", "bold");
    pdf.text("Subsection Scores", M, y);
    y += 9;

    answeredSubsections.forEach((sub) => {
      if (y > H - 20) {
        pdf.addPage();
        y = 20;
      }

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(55, 65, 81);
      pdf.text(sub.label, M, y);

      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(38, 56, 66);
      pdf.text(sub.value.toFixed(1), W - M, y, { align: "right" });

      const barW = 35;
      const barX = W - M - barW - 12;
      pdf.setFillColor(229, 231, 235);
      pdf.roundedRect(barX, y - 3.5, barW, 3, 0.5, 0.5, "F");
      pdf.setFillColor(38, 56, 66);
      pdf.roundedRect(barX, y - 3.5, barW * (sub.value / 5), 3, 0.5, 0.5, "F");
      y += 8;
    });
  }

  // ── RECOMMENDATIONS ───────────────────��─────────────────────────
  if (sections.length > 0) {
    if (y > H - 70) {
      pdf.addPage();
      y = 20;
    }
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.3);
    pdf.line(M, y, W - M, y);
    y += 10;

    pdf.setTextColor(38, 56, 66);
    pdf.setFontSize(13);
    pdf.setFont("helvetica", "bold");
    pdf.text("Recommended Next Steps", M, y);
    y += 6;
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(107, 114, 128);
    pdf.text(
      "Based on scores, the following areas are recommended for priority attention.",
      M,
      y,
    );
    y += 10;

    [...sections]
      .sort((a, b) => a.score - b.score)
      .slice(0, 3)
      .forEach((s, i) => {
        if (y > H - 35) {
          pdf.addPage();
          y = 20;
        }

        pdf.setFillColor(38, 56, 66);
        pdf.circle(M + 3.5, y - 1, 3.5, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "bold");
        pdf.text(String(i + 1), M + 3.5, y, { align: "center" });

        pdf.setTextColor(30, 32, 44);
        pdf.setFontSize(10.5);
        pdf.text(`${s.name}  (Score: ${s.score}/5)`, M + 10, y);
        y += 6;

        pdf.setTextColor(107, 114, 128);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        const rec =
          s.score < 3
            ? `Prioritise building strong foundational practices in ${s.name}. Consider dedicated leadership development and structured programming.`
            : `Deepen existing strengths in ${s.name} by decentralising responsibility and empowering more members to lead.`;
        const recLines = pdf.splitTextToSize(rec, CW - 10);
        pdf.text(recLines, M + 10, y);
        y += recLines.length * 4.5 + 8;
      });
  }

  // ── REFLECTION RESPONSES ────────────────────────────────────────
  const reflections = responses.filter(
    (r) => r.reflection?.reflection_text || r.reflection?.self_rating,
  );

  if (reflections.length > 0) {
    pdf.addPage();
    y = 20;

    pdf.setFillColor(38, 56, 66);
    pdf.rect(0, 0, W, 17, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(13);
    pdf.setFont("helvetica", "bold");
    pdf.text("Leader Reflection Responses", M, 11);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(160, 175, 185);
    pdf.text(
      "Personal self-ratings and written reflections submitted during the Framework.",
      M,
      16,
    );
    y = 27;

    let lastSection = null;

    reflections.forEach((r) => {
      if (y > H - 35) {
        pdf.addPage();
        y = 20;
      }

      if (r.section?.name !== lastSection) {
        lastSection = r.section?.name;
        if (y > 27) y += 5;
        pdf.setFillColor(60, 72, 80);
        pdf.rect(M, y - 4, CW, 9, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.text(lastSection, M + 3, y + 2);
        y += 14;
      }

      pdf.setTextColor(30, 32, 44);
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.text(r.subsection?.name || "", M, y);

      if (r.reflection?.self_rating) {
        const ratingText = `Self-rating: ${r.reflection.self_rating} / 5`;
        pdf.setTextColor(78, 72, 120);
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "normal");
        pdf.text(ratingText, W - M, y, { align: "right" });

        const dotY = y - 1.5;
        const dotStartX = W - M - pdf.getTextWidth(ratingText) - 20;
        for (let d = 1; d <= 5; d++) {
          pdf.setFillColor(
            d <= r.reflection.self_rating ? 78 : 210,
            d <= r.reflection.self_rating ? 72 : 215,
            d <= r.reflection.self_rating ? 120 : 225,
          );
          pdf.circle(dotStartX + (d - 1) * 3.5, dotY, 1.4, "F");
        }
      }
      y += 7;

      if (r.reflection?.reflection_text) {
        pdf.setDrawColor(208, 204, 218);
        pdf.setLineWidth(0.5);
        pdf.line(M + 2, y, M + 2, y + 25);

        pdf.setTextColor(75, 85, 99);
        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(9.5);
        const lines = pdf.splitTextToSize(
          `"${r.reflection.reflection_text}"`,
          CW - 8,
        );

        if (y + lines.length * 4.5 > H - 18) {
          pdf.addPage();
          y = 20;
        }

        pdf.text(lines, M + 6, y);
        y += lines.length * 4.5 + 5;
      }

      pdf.setDrawColor(240, 240, 242);
      pdf.setLineWidth(0.2);
      pdf.line(M, y, W - M, y);
      y += 7;
    });
  }

  // ── FOOTER ON EVERY PAGE ────────────────────────────────────────
  const pageCount = pdf.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    pdf.setPage(p);
    pdf.setFillColor(38, 56, 66);
    pdf.rect(0, H - 10, W, 10, "F");
    pdf.setTextColor(148, 163, 184);
    pdf.setFontSize(7);
    pdf.setFont("helvetica", "normal");
    pdf.text(
      "The Gospel and Church Health Framework  |  City to City Asia Pacific",
      M,
      H - 4,
    );
    pdf.text(`Page ${p} of ${pageCount}`, W - M, H - 4, { align: "right" });
  }

  const safeName = row.name.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, "");
  pdf.save(`GCCHF_Health_Report_${safeName}.pdf`);
};
